<template>
  <div class="transaction-data-page">
    <!-- 标题 + 时间范围 -->
    <div class="page-header">
      <h2 class="page-title">交易数据</h2>
      <div class="date-filter">
        <span class="date-filter-label">
          <el-icon><Calendar /></el-icon>
          <span>时间范围</span>
        </span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="-"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
          format="YYYY/MM/DD"
          class="date-range-picker"
          :shortcuts="dateShortcuts"
          :disabled-date="disabledDate"
          @change="onDateChange"
        />
      </div>
    </div>

    <!-- 8 个指标卡片 -->
    <div class="kpi-grid">
      <div
        v-for="item in kpiList"
        :key="item.key"
        class="kpi-card"
      >
        <div class="kpi-content">
          <span class="kpi-label">
            {{ item.label }}
            <el-tooltip placement="top" effect="light">
              <template #content>
                <span class="tooltip-content">{{ item.tooltip }}</span>
              </template>
              <el-icon class="kpi-info-icon"><QuestionFilled /></el-icon>
            </el-tooltip>
          </span>
          <span class="kpi-value">{{ item.displayValue }}</span>
        </div>
        <div class="kpi-icon-wrap">
          <el-icon class="kpi-icon"><component :is="item.icon" /></el-icon>
        </div>
      </div>
    </div>

    <!-- 销售曲线 + 商品排行 -->
    <div class="bottom-section">
      <el-card class="chart-card" shadow="never">
        <template #header>
          <span>成交总额</span>
        </template>
        <div class="chart-wrap" v-loading="chartLoading">
          <v-chart
            v-if="chartOption"
            :option="chartOption"
            class="sales-chart"
            autoresize
          />
          <div v-else-if="!chartLoading" class="chart-empty">暂无数据</div>
        </div>
      </el-card>
      <el-card class="table-card" shadow="never">
        <template #header>
          <span>商品销售排行TOP10</span>
        </template>
        <el-table
          :data="top10List"
          v-loading="tableLoading"
          style="width: 100%"
          max-height="340"
        >
          <el-table-column prop="rank" label="排行" width="70" align="center" />
          <el-table-column label="商品信息" min-width="280">
            <template #default="{ row }">
              <div class="product-info">
                <img
                  v-if="row.spuImageUrl"
                  :src="row.spuImageUrl"
                  class="product-img"
                  alt=""
                />
                <div class="product-text">
                  <span class="product-name" :title="row.spuName">{{ row.spuName || '--' }}</span>
                  <span class="product-price" v-if="row.priceDisplay">{{ formatDisplayPrice(row.priceDisplay) }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="销售金额" width="120" align="right">
            <template #default="{ row }">
              {{ formatCurrency(row.salesAmount) }}
            </template>
          </el-table-column>
          <el-table-column prop="salesVolume" label="销量" width="90" align="center">
            <template #default="{ row }">
              {{ row.salesVolume ?? '--' }}
            </template>
          </el-table-column>
        </el-table>
        <div v-if="!tableLoading && !top10List.length" class="table-empty">暂无数据</div>
      </el-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useCurrentAppInstance } from '@zto/zpage'
import { QuestionFilled, Calendar, Coin, Wallet, Tickets, User, RefreshLeft, DocumentDelete, TrendCharts, Sell } from '@element-plus/icons-vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  GridComponent
} from 'echarts/components'
import dayjs from 'dayjs'
import { yuanMoney } from '@/utils/formatter'
import { ElMessage } from 'element-plus'

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent
])

const app = useCurrentAppInstance()

/** 最长可查询一年内；单次最多 30 天 */
const MAX_RANGE_DAYS = 30
const MAX_QUERY_DAYS = 365

// 默认近 7 天
const defaultRange = (): [string, string] => {
  const end = dayjs()
  const start = end.subtract(6, 'day')
  return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
}

const dateRange = ref<string[]>(defaultRange())

// 快捷选项：今天、近7天、近30天
const dateShortcuts = [
  {
    text: '今天',
    value: () => {
      const t = dayjs().format('YYYY-MM-DD')
      return [t, t] as [string, string]
    }
  },
  {
    text: '近7天',
    value: () => {
      const end = dayjs()
      const start = end.subtract(6, 'day')
      return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')] as [string, string]
    }
  },
  {
    text: '近30天',
    value: () => {
      const end = dayjs()
      const start = end.subtract(29, 'day')
      return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')] as [string, string]
    }
  }
]

// 仅允许选择一年内、且不晚于今天的日期
function disabledDate (date: Date) {
  const d = dayjs(date)
  const today = dayjs().endOf('day')
  const oneYearAgo = dayjs().subtract(MAX_QUERY_DAYS, 'day').startOf('day')
  return d.isAfter(today) || d.isBefore(oneYearAgo)
}

// 8 个指标说明
const kpiMeta = [
  {
    key: 'gmv',
    label: '营业额（GMV）',
    tooltip: '平台一定时间内的总交易额（含优惠券、商品折扣，商品原价），订单原价',
    icon: Coin
  },
  {
    key: 'orderTotalAmount',
    label: '成交总额',
    tooltip: '平台一定时间内的实付金额（含退款订单）',
    icon: Wallet
  },
  {
    key: 'orderCount',
    label: '成交订单数',
    tooltip: '平台一定时间内的总交易订单（含退款订单）',
    icon: Tickets
  },
  {
    key: 'buyerCount',
    label: '成交人数',
    tooltip: '平台一定时间内的总交易人数，去重',
    icon: User
  },
  {
    key: 'refundAmount',
    label: '退款金额',
    tooltip: '平台一定时间内的总退款金额',
    icon: RefreshLeft
  },
  {
    key: 'refundOrderCount',
    label: '退款订单数',
    tooltip: '平台一定时间内的总退款订单数',
    icon: DocumentDelete
  },
  {
    key: 'actualIncome',
    label: '实际收入',
    tooltip: '剔除退款后的真实入账金额，实付总额-退款金额',
    icon: TrendCharts
  },
  {
    key: 'avgOrderPrice',
    label: '客单价',
    tooltip: '平均每个订单的金额，实付总额/成交订单数',
    icon: Sell
  }
]

const stats = ref<Record<string, number | string | null>>({
  gmv: null,
  orderTotalAmount: null,
  orderCount: null,
  buyerCount: null,
  refundAmount: null,
  refundOrderCount: null,
  actualIncome: null,
  avgOrderPrice: null
})

function formatMoney (val: number | string | null | undefined): string {
  if (val == null || val === '') return '0.00'
  const n = Number(val)
  if (Number.isNaN(n)) return '0.00'
  return yuanMoney(n, { decimals: 2, thousandSep: ',', emptyText: '0.00' })
}

function formatCurrency (val: number | string | null | undefined): string {
  return `¥ ${formatMoney(val)}`
}

function formatDisplayPrice (val: string | number): string {
  const text = String(val).trim()
  if (!text) return ''
  if (text.startsWith('¥')) {
    return `¥ ${text.slice(1).trim()}`
  }
  return `¥ ${text}`
}

const kpiList = computed(() => {
  return kpiMeta.map(m => {
    const raw = stats.value[m.key]
    let displayValue = '--'
    if (raw != null && raw !== '') {
      if (m.key === 'orderCount' || m.key === 'buyerCount' || m.key === 'refundOrderCount') {
        displayValue = String(Number(raw))
      } else {
        displayValue = formatCurrency(raw as number)
      }
    }
    return { ...m, displayValue }
  })
})

const chartData = ref<{ date: string; price: number }[]>([])
const chartLoading = ref(false)
const chartOption = computed(() => {
  const data = chartData.value
  if (!data || !data.length) return null
  const dates = data.map(d => {
    const dStr = d.date
    if (dStr && dStr.length >= 10) return dStr.slice(5, 10).replace('-', '/')
    return dStr
  })
  const values = data.map(d => Number(d.price) || 0)
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        if (!params || !params.length) return ''
        const p = params[0]
        const idx = p.dataIndex
        const dateStr = data[idx]?.date || ''
        const value = values[idx]
        return `${dateStr}<br/>成交总额<br/>¥ ${value.toFixed(2)}`
      }
    },
    grid: { left: 50, right: 24, top: 24, bottom: 36 },
    xAxis: { type: 'category', data: dates, boundaryGap: false },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: (v: number) => `¥ ${v}` }
    },
    series: [{ type: 'line', data: values, smooth: true, symbol: 'circle', symbolSize: 6 }]
  }
})

const top10List = ref<any[]>([])
const tableLoading = ref(false)

function getParams () {
  const [start, end] = dateRange.value || defaultRange()
  return {
    startTime: start ? start + ' 00:00:00' : '',
    endTime: end ? end + ' 23:59:59' : ''
  }
}

async function fetchStats () {
  const params = getParams()
  try {
    // 服务端 Result<T> 经 afterFetchResponse 后，返回的是 result 字段（即 TransactionStatsResp）
    const res: any = await app.request({
      api: '/admin/dataversion/transactionStats',
      method: 'POST',
      data: params
    })

    if (res && typeof res === 'object') {
      stats.value = {
        gmv: res.gmv,
        orderTotalAmount: res.orderTotalAmount,
        orderCount: res.orderCount,
        buyerCount: res.buyerCount,
        refundAmount: res.refundAmount,
        refundOrderCount: res.refundOrderCount,
        actualIncome: res.actualIncome,
        avgOrderPrice: res.avgOrderPrice
      }
    }
  } catch (e) {
    console.error('transactionStats error', e)
  }
}

async function fetchChart () {
  chartLoading.value = true
  try {
    const params = getParams()
    // 服务端返回 SalesCurveResp，afterFetchResponse 后 res = result，即 { data: SalesCurvePointResp[] }
    const res: any = await app.request({
      api: '/admin/dataversion/salesCurve',
      method: 'POST',
      data: params
    })
    const list = Array.isArray(res?.data) ? res.data : []
    chartData.value = list.map((p: any) => ({ date: p.date || '', price: Number(p.price) || 0 }))
  } catch (e) {
    console.error('salesCurve error', e)
    chartData.value = []
  } finally {
    chartLoading.value = false
  }
}

async function fetchTop10 () {
  tableLoading.value = true
  try {
    const params = getParams()
    // 服务端返回 ProductSalesTop10Resp，afterFetchResponse 后 res = result，即 { data: ProductSalesTop10ItemResp[] }
    const res: any = await app.request({
      api: '/admin/dataversion/productSalesTop10',
      method: 'POST',
      data: params
    })
    top10List.value = Array.isArray(res?.data) ? res.data : []
  } catch (e) {
    console.error('productSalesTop10 error', e)
    top10List.value = []
  } finally {
    tableLoading.value = false
  }
}

function onDateChange (val: string[] | null) {
  if (!val || val.length !== 2) return
  const [startStr, endStr] = val
  const start = dayjs(startStr)
  const end = dayjs(endStr)
  const days = end.diff(start, 'day') + 1
  if (days > MAX_RANGE_DAYS) {
    const newStart = end.subtract(MAX_RANGE_DAYS - 1, 'day').format('YYYY-MM-DD')
    dateRange.value = [newStart, endStr]
    ElMessage.warning(`单次最多查询 ${MAX_RANGE_DAYS} 天，已自动调整为最近 ${MAX_RANGE_DAYS} 天`)
  }
  fetchStats()
  fetchChart()
  fetchTop10()
}

function loadAll () {
  fetchStats()
  fetchChart()
  fetchTop10()
}

onMounted(loadAll)
</script>

<style lang="scss" scoped>
.transaction-data-page {
  padding: 16px 24px;
  background: #f5f7fa;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  .page-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }

  .date-filter {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .date-filter-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #606266;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  .date-range-picker {
    width: 260px;
  }
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.kpi-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ebeef5;
  min-height: 72px;

  .kpi-content {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .kpi-label {
      font-size: 14px;
      color: #909399;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      .kpi-info-icon {
        font-size: 14px;
        color: #c0c4cc;
        cursor: help;
      }
    }

    .kpi-value {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
    }
  }

  .kpi-icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #409eff;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;

    .kpi-icon {
      font-size: 20px;
    }
  }
}

.tooltip-content {
  max-width: 260px;
  display: inline-block;
  line-height: 1.5;
}

.bottom-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.chart-card,
.table-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;

  :deep(.el-card__header) {
    font-weight: 600;
    color: #303133;
  }
}

.chart-wrap {
  height: 320px;
  position: relative;
}

.sales-chart {
  width: 100%;
  height: 100%;
}

.chart-empty,
.table-empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
}

.product-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  .product-img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .product-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .product-name {
    font-size: 14px;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .product-price {
    font-size: 12px;
    color: #909399;
  }
}
</style>
